import { useState } from "react";
import { StyleSheet, useWindowDimensions } from "react-native";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";

import { RegularExpressions } from "../../constants";

import { Spacing } from "../../styles";

import { authActions } from "../../store/slices/auth";
import { autoLogin } from "../../store/actions/auth";

import { useLanguage } from "../../hooks";

import { auth } from "../../models";

import { Wrapper, Screen, Container, FluidView, LoadingModal, H3, Field } from "../../components/UI";
import { StyledModal } from "../../components/UI/Styled";
import FormTextInput from "../../components/Auth/FormTextInput";
import FormButton from "../../components/Auth/FormButton";

const ResetPasswordScreen = ({ navigation, route }) => {
    const token = route.params?.token;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(true);
    const [showConfirmPassword, setShowConfirmPassword] = useState(true);

    const layout = useWindowDimensions();
    const isWeb = layout.width > 768;

    const dispatch = useDispatch();

    const {
        control,
        handleSubmit,
        getValues,
        formState: { errors },
    } = useForm({
        mode: "onBlur",
        defaultValues: { password: "", confirmPassword: "" },
    });

    const { translate } = useLanguage();

    const toggleIsLoading = () => {
        setIsLoading((prevIsLoading) => !prevIsLoading);
    };

    const toggleShowPassword = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    const toggleShowConfirmPassword = () => {
        setShowConfirmPassword((prevShowConfirmPassword) => !prevShowConfirmPassword);
    };

    const removeError = () => {
        setError(null);
    };

    const resetPasswordHandler = async ({ password, confirmPassword }) => {
        try {
            toggleIsLoading();

            const { data: resetPasswordData } = await auth.resetPassword({
                token,
                password,
                confirm_password: confirmPassword,
            });

            dispatch(
                authActions.authenticate({
                    token: resetPasswordData.access,
                    expirationTime: resetPasswordData.tokenlifetime,
                    refresh: resetPasswordData.refresh,
                })
            );

            await dispatch(autoLogin(resetPasswordData.access));

            toggleIsLoading();

            navigation.navigate("ProjectNav");
        } catch (e) {
            toggleIsLoading();

            switch (e?.response?.status) {
                case 400:
                    setError(translate("sentence", "PASSWORDS_DO_NOT_MATCH"));
                    break;
                default:
                    setError(translate("sentence", "SOMETHING_WENT_WRONG"));
                    break;
            }
        }
    };

    return (
        <Wrapper navbar={false}>
            <LoadingModal visible={isLoading} />
            <StyledModal
                modalType="alert"
                visible={!!error}
                size="medium"
                title={translate("word", "ERROR")}
                titleColor="danger"
                onAccept={removeError}
                onPressOut={removeError}
                onRequestClose={removeError}
            >
                {error}
            </StyledModal>
            <Screen style={!isWeb ? styles.screenMobile : undefined}>
                <Container style={styles.container}>
                    <H3>{translate("sentence", "RESET_PASSWORD")}</H3>
                    <Container style={styles.InnerContainer}>
                        <FluidView style={styles.inputsContainer}>
                            <Controller
                                control={control}
                                name="password"
                                rules={{
                                    required: true,
                                    pattern: RegularExpressions.MODERATE_PASSWORD,
                                }}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <Field
                                        label={
                                            errors.password?.type === "required"
                                                ? translate("sentence", "PLEASE_ENTER_YOUR_PASSWORD")
                                                : errors.password?.type === "pattern"
                                                ? translate("sentence", "PLEASE_ENTER_A_MODERATE_PASSWORD")
                                                : ""
                                        }
                                        labelColor={errors.password ? "danger" : undefined}
                                        style={styles.formField}
                                    >
                                        <FormTextInput
                                            inputType={errors.password ? "danger" : ""}
                                            showPassword={showPassword}
                                            returnKeyType="next"
                                            autoCorrect={false}
                                            autoCapitalize="none"
                                            placeholder={translate("word", "PASSWORD")}
                                            value={value}
                                            onShowPassword={toggleShowPassword}
                                            onHidePassword={toggleShowPassword}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                        />
                                    </Field>
                                )}
                            />
                            <Controller
                                control={control}
                                name="confirmPassword"
                                rules={{
                                    required: true,
                                    validate: (value) => value === getValues("password"),
                                }}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <Field
                                        label={
                                            errors.confirmPassword?.type === "required"
                                                ? translate("sentence", "PLEASE_ENTER_YOUR_PASSWORD")
                                                : errors.confirmPassword?.type === "validate"
                                                ? translate("sentence", "PASSWORDS_DO_NOT_MATCH")
                                                : ""
                                        }
                                        labelColor={errors.confirmPassword ? "danger" : undefined}
                                    >
                                        <FormTextInput
                                            inputType={errors.confirmPassword ? "danger" : ""}
                                            showPassword={showConfirmPassword}
                                            returnKeyType="done"
                                            autoCorrect={false}
                                            autoCapitalize="none"
                                            placeholder={translate("word", "CONFIRM_PASSWORD")}
                                            value={value}
                                            onShowPassword={toggleShowConfirmPassword}
                                            onHidePassword={toggleShowConfirmPassword}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                        />
                                    </Field>
                                )}
                            />
                        </FluidView>
                        <FormButton
                            disabled={isLoading}
                            isLoading={isLoading}
                            style={styles.formButton}
                            onPress={handleSubmit(resetPasswordHandler)}
                        >
                            {translate("verb", "RESET")}
                        </FormButton>
                    </Container>
                </Container>
            </Screen>
        </Wrapper>
    );
};

const styles = StyleSheet.create({
    container: {
        maxWidth: Spacing.screen.maxWidth,
    },
    InnerContainer: {
        width: "90%",
        maxWidth: Spacing.screen.maxWidth - 40,
        marginTop: 20,
    },
    inputsContainer: {
        marginTop: 50,
    },
    formField: {
        marginBottom: 20,
    },
    formButton: {
        marginTop: 20,
    },
    screenMobile: {
        paddingHorizontal: 0,
        paddingVertical: 0,
    },
});

export default ResetPasswordScreen;
